import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PlaceIcon from '@mui/icons-material/Place';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { CtaButton } from '@tassomutuo-backoffice/ui';
import { ExternalLeadQuality, ExternalLeadStatus } from '@tassomutuo-backoffice/types';
import ExternalLeadStatusChip from './ExternalLeadStatusChip';
import ExternalLeadQualityChip from './ExternalLeadQualityChip';
import useDateFormat from '../../../../hooks/useDateFormat';

interface IProps {
  status: ExternalLeadStatus;
  quality?: ExternalLeadQuality;
  createdAt?: string;
  fullName: string;
  email?: string;
  prefix?: string;
  phone?: string;
  source?: string;
  productInfo?: React.ReactNode;
  onOpenDetails: () => void;
}

const ExternalLeadCardShell = ({
  status,
  quality,
  createdAt,
  fullName,
  email,
  prefix,
  phone,
  source,
  productInfo,
  onOpenDetails,
}: IProps) => {
  const { formatDate } = useDateFormat();

  const fullPhone =
    prefix && phone ? `${prefix} ${phone}` : prefix || phone || undefined;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'white',
        mx: 4,
        my: 2,
        p: 2,
        borderRadius: 1,
        boxShadow: (th) => `0 1px 2px ${th.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          pb: 1,
          borderBottom: (th) => `1px solid ${th.palette.divider}`,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <ExternalLeadStatusChip status={status} />
          <ExternalLeadQualityChip quality={quality} />
        </Stack>
        {createdAt && (
          <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary">
            <CalendarTodayIcon fontSize="small" />
            <Typography variant="body2">
              {formatDate(createdAt, 'dd/MM/yyyy HH:mm')}
            </Typography>
          </Stack>
        )}
      </Box>

      <Box sx={{ pt: 2, pb: 1 }}>
        <Typography variant="h6" fontWeight="bold" textTransform="uppercase">
          {fullName || '—'}
        </Typography>
      </Box>

      <Stack
        direction="row"
        spacing={3}
        flexWrap="wrap"
        rowGap={1}
        sx={{ color: 'text.secondary', mb: 1 }}
      >
        {email && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <EmailIcon fontSize="small" />
            <Typography variant="body2">{email}</Typography>
          </Stack>
        )}
        {fullPhone && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <PhoneIcon fontSize="small" />
            <Typography variant="body2">{fullPhone}</Typography>
          </Stack>
        )}
      </Stack>

      {productInfo && <Box sx={{ mb: 1 }}>{productInfo}</Box>}

      {source && (
        <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary" sx={{ mb: 1 }}>
          <PlaceIcon fontSize="small" />
          <Typography variant="body2">{source}</Typography>
        </Stack>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <CtaButton variant="contained" onClick={onOpenDetails}>
          Apri dettagli
        </CtaButton>
      </Box>
    </Box>
  );
};

export default ExternalLeadCardShell;
